import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useFormHook from '@shared/hooks/useFormHook';
import { useForm } from 'react-hook-form';
import { locations } from '@modules/organizationalStructure/locations/services/locations';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Select, { components } from 'react-select';
import LoadingButton from '@shared/components/LoadingButton';

export default function LocationsForm({ onClose, editData, selectedCountry, handleConfirm }) {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const [loading, setLoading] = useState(false);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [isLoadingCities, setIsLoadingCities] = useState(false);
    const [searchInput, setSearchInput] = useState('');


    const validationSchema = yup.object().shape({
        abbreviation: yup.string().required(t('common.required')),
        title: yup.string().required(t('common.required')),
        cityById: yup.string().required(t('common.required'))
    });
    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema)
    });
    const watchedValues = watch();

    const searchCities = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setCities([]);
            return;
        }

        setIsLoadingCities(true);
        try {
            const citiesData = await locations.searchCities(searchTerm);

            let citiesArray = [];
            if (Array.isArray(citiesData)) {
                citiesArray = citiesData;
            } else if (citiesData && Array.isArray(citiesData.content)) {
                citiesArray = citiesData.content;
            } else if (citiesData && citiesData._embedded && Array.isArray(citiesData._embedded.cities)) {
                citiesArray = citiesData._embedded.cities;
            } else if (citiesData && typeof citiesData === 'object') {
                const possibleArray = Object.values(citiesData).find(val => Array.isArray(val));
                if (possibleArray) {
                    citiesArray = possibleArray;
                }
            }

            setCities(citiesArray);
        } catch (error) {
            console.error('Error searching cities:', error);
            setCities([]);
        } finally {
            setIsLoadingCities(false);
        }
    };

    const cityOptions = Array.isArray(cities) ? cities.map(city => ({
        value: city.id,
        label: city.name,
        data: city
    })) : [];

    useEffect(() => {
        if (editData) {

            setValue('codeLocation', editData.locationCode || '');
            setValue('abbreviation', editData.description || '');
            setValue('title', editData.name || '');
            setValue('address1', editData.address1 || '');
            setValue('address2', editData.address2 || '');
            setValue('address3', editData.address3 || '');
            setValue('status', editData.active ? '0' : '1');
            setValue('postal', editData.postal || '');
            setValue('region', editData.regionName || '');
            setValue('subregion', editData.subRegionName || '');
            setValue('cityById', editData.cityById || '');

            if (editData.cityById && editData.cityName) {
                const cityOption = {
                    value: editData.cityById,
                    label: editData.cityName,
                    data: {
                        id: editData.cityById,
                        name: editData.cityName,
                        postal: editData.postal,
                        regionId: editData.regionId,
                        cSubRegionId: editData.subRegionId,
                        regionName: editData.regionName,
                        subRegionName: editData.subRegionName
                    }
                };
                setSelectedCity(cityOption);
            }
        }
    }, [editData, setValue]);

    const handleClose = () => {
        onClose();
    };

    const submitForm = async (data) => {
        setLoading(true);
        try {


            const locationData = {
                subRegionId: selectedCity?.data?.cSubRegionId || null,
                countryCode: selectedCountry?.countryCode || 'BG',
                cityById: data.cityById ? parseInt(data.cityById) : null,
                regionId: selectedCity?.data?.regionId || null,
                address1: data.address1,
                address2: data.address2,
                address3: data.address3,
                active: data.status === '0',
                name: data.title || '',
                description: data.abbreviation || '',
                postal: data.postal || '',
                locationCode: data.codeLocation || ''
            };

            await handleConfirm(locationData);

        } catch (error) {
            console.error('Error submitting form:', error);
            notifyError(t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    const handleCitySelection = (selectedOption) => {
        if (selectedOption) {
            setValue('cityById', selectedOption.value);
            setSelectedCity(selectedOption);

            const cityData = selectedOption.data;
            if (cityData) {

                if (cityData.postal) {
                    setValue('postal', cityData.postal);
                }

                if (cityData.regionName) {
                    setValue('region', cityData.regionName);
                }
                if (cityData.subRegionName) {
                    setValue('subregion', cityData.subRegionName);
                }

                setSearchInput('');
                setCities([]);
            }
        } else {
            setValue('cityById', '');
            setSelectedCity(null);
            setSearchInput('');
        }
    }

    // Custom ClearIndicator component to prevent modal from closing when clicking clear button
    const ClearIndicator = (props) => {
        const { children = null, innerProps } = props;
        return (
            <components.ClearIndicator
                {...props}
                innerProps={{
                    ...innerProps,
                    onMouseDown: (e) => {
                        e.stopPropagation();
                        if (innerProps.onMouseDown) {
                            innerProps.onMouseDown(e);
                        }
                    },
                    onClick: (e) => {
                        e.stopPropagation();
                        if (innerProps.onClick) {
                            innerProps.onClick(e);
                        }
                    }
                }}
            >
                {children}
            </components.ClearIndicator>
        );
    };
    const customSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '24px',
            height: '24px',
            fontSize: '14px',
            padding: '0',
        }),
        valueContainer: (base) => ({
            ...base,
            height: '24px',
            padding: '0 4px',
        }),
        input: (base) => ({
            ...base,
            margin: '0',
            padding: '0',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '24px',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '0 4px',
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: '0 4px',
        }),
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={handleClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('common.edit') : t('common.add')} {t('organizationalStructure.locations.title')}
                        </h4>
                        <form id="locationsForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="flex-row gap-5">
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.codeLocation')}</span>
                                        <input
                                            type="text"
                                            maxLength={15}
                                            className="small-input"
                                            {...register('codeLocation')}
                                            value={watchedValues.codeLocation || ''}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.abbreviation')}<span className="required">*</span></span>
                                        <input
                                            type="text"
                                            maxLength={20}
                                            className="small-input"
                                            {...register('abbreviation')}
                                            value={watchedValues.abbreviation || ''}
                                        />
                                        {errors.abbreviation && <p className="invalid-feedback">{errors.abbreviation.message}</p>}
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.title')}<span className="required">*</span></span>
                                        <input
                                            type="text"
                                            maxLength={100}
                                            className="small-input"
                                            {...register('title')}
                                            value={watchedValues.title || ''}
                                        />
                                        {errors.title && <p className="invalid-feedback">{errors.title.message}</p>}
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.country')}</span>
                                        <select disabled={true} className="small-select" {...register('country')}>
                                            {selectedCountry && <option value={selectedCountry.countryCode}>{selectedCountry.countryNameBg}</option>}
                                        </select>
                                    </div>

                                </div>
                                <div className="form-section width-30">
                                    <div className="form-group">
                                        <span className="span-bold">{t('persons.form.city')}<span className="required">*</span></span>
                                        <Select
                                            name='cityById'
                                            placeholder={t('users.table.search.startType')}
                                            options={cityOptions}
                                            value={selectedCity}
                                            isDisabled={!selectedCountry}
                                            onChange={handleCitySelection}
                                            onInputChange={(inputValue) => {
                                                setSearchInput(inputValue);
                                                if (inputValue && inputValue.length >= 2) {
                                                    searchCities(inputValue);
                                                } else {
                                                    setCities([]);
                                                }
                                                return inputValue;
                                            }}
                                            isLoading={isLoadingCities}
                                            isSearchable={true}
                                            isClearable={true}
                                            noOptionsMessage={() => t('common.select')}
                                            loadingMessage={() => t('common.loading')}
                                            styles={customSelectStyles}
                                            components={{ ClearIndicator }}
                                        />
                                        {errors.cityById && <p className="invalid-feedback">{errors.cityById.message}</p>}
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.district')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            disabled={true}
                                            {...register('region')}
                                            value={watchedValues.region || ''}
                                            placeholder={t('organizationalStructure.locations.form.autoPopulated')}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.municipality')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            disabled={true}
                                            {...register('subregion')}
                                            value={watchedValues.subregion || ''}
                                            placeholder={t('organizationalStructure.locations.form.autoPopulated')}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.postalCode')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            disabled={true}
                                            {...register('postal')}
                                            value={watchedValues.postal || ''}
                                            placeholder={t('organizationalStructure.locations.form.autoPopulated')}
                                        />
                                    </div>
                                </div>
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.street')}</span>
                                        <input
                                            maxLength={60}
                                            type="text"
                                            className="small-input"
                                            {...register('address1')}
                                            value={watchedValues.address1 || ''}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.streetNo')}</span>
                                        <input
                                            maxLength={60}
                                            type="text"
                                            className="small-input"
                                            {...register('address2')}
                                            value={watchedValues.address2 || ''}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.streetFloor')}</span>
                                        <input
                                            maxLength={60}
                                            type="text"
                                            className="small-input"
                                            {...register('address3')}
                                            value={watchedValues.address3 || ''}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.status')}</span>
                                        <select
                                            className="small-select"
                                            {...register('status')}
                                            value={watchedValues.status || '0'}
                                        >
                                            <option value="0">{t('organizationalStructure.locations.form.active')}</option>
                                            <option value="1">{t('organizationalStructure.locations.form.inactive')}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                {/* <button type="submit" className="btn btn-primary" disabled={loading}>
                                        {loading ? t('common.saving') :  t('common.save')}
                                    </button> */}
                                <LoadingButton type="submit" className="btn btn-primary" loading={loading} disabled={loading}>{t('common.save')}</LoadingButton>
                                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                    {t('common.cancel')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div >
    );
}